import { gql } from '@apollo/client';

import {
  MARKETING_CAMPAIGN_ALIAS_FIELDS,
  MARKETING_CHANNEL_RULE_FIELDS,
  MARKETING_COURSE_ALIAS_FIELDS,
  MARKETING_OFFER_RULE_FIELDS,
} from '../fragments/marketing';

/**
 * ТЗ 0088 (этап 1) — правка справочников раздела «Маркетинг» (Admin и
 * Marketing), «Обновить сейчас» и ручная правка продажи (только Admin).
 *
 * Имена операций намеренно НЕ совпадают с полями бэкенда
 * (`saveMarketingChannelRule` → `StoreMarketingChannelRule`,
 * `deleteMarketingChannelRule` → `RemoveMarketingChannelRule` и т. д.).
 *
 * Бизнес-отказ — в `customErrors { field code message }` (промис успешен);
 * отказ прав — GraphQL-ошибка (промис отклоняется). Правка ставит пересчёт
 * сводок: новые цифры появятся, когда вырастет `dataVersion`.
 */

/** Admin: поставить сбор источников (все или перечисленные). */
export const RefreshMarketingSourcesNowMutation = gql`
  mutation RefreshMarketingSourcesNow($sources: [String!]) {
    refreshMarketingSources(sources: $sources) {
      success
      customErrors {
        field
        code
        message
      }
      queued
      skipped {
        source
        reason
      }
      rebuildQueued
    }
  }
`;

export const StoreMarketingChannelRuleMutation = gql`
  mutation StoreMarketingChannelRule($input: MarketingChannelRuleInput!) {
    saveMarketingChannelRule(input: $input) {
      success
      customErrors {
        field
        code
        message
      }
      rule {
        ...MarketingChannelRuleFields
      }
    }
  }
  ${MARKETING_CHANNEL_RULE_FIELDS}
`;

export const RemoveMarketingChannelRuleMutation = gql`
  mutation RemoveMarketingChannelRule($id: ID!) {
    deleteMarketingChannelRule(id: $id) {
      success
      customErrors {
        field
        code
        message
      }
    }
  }
`;

export const StoreMarketingCourseFamilyMutation = gql`
  mutation StoreMarketingCourseFamily($input: MarketingCourseFamilyInput!) {
    saveMarketingCourseFamily(input: $input) {
      success
      customErrors {
        field
        code
        message
      }
      family {
        id
        slug
        name
        isSold
        sort
        aliases {
          ...MarketingCourseAliasFields
        }
      }
    }
  }
  ${MARKETING_COURSE_ALIAS_FIELDS}
`;

export const StoreMarketingCourseAliasMutation = gql`
  mutation StoreMarketingCourseAlias($input: MarketingCourseAliasInput!) {
    saveMarketingCourseAlias(input: $input) {
      success
      customErrors {
        field
        code
        message
      }
      alias {
        ...MarketingCourseAliasFields
      }
    }
  }
  ${MARKETING_COURSE_ALIAS_FIELDS}
`;

export const RemoveMarketingCourseAliasMutation = gql`
  mutation RemoveMarketingCourseAlias($id: ID!) {
    deleteMarketingCourseAlias(id: $id) {
      success
      customErrors {
        field
        code
        message
      }
    }
  }
`;

export const StoreMarketingOfferRuleMutation = gql`
  mutation StoreMarketingOfferRule($input: MarketingOfferRuleInput!) {
    saveMarketingOfferRule(input: $input) {
      success
      customErrors {
        field
        code
        message
      }
      rule {
        ...MarketingOfferRuleFields
      }
    }
  }
  ${MARKETING_OFFER_RULE_FIELDS}
`;

export const RemoveMarketingOfferRuleMutation = gql`
  mutation RemoveMarketingOfferRule($id: ID!) {
    deleteMarketingOfferRule(id: $id) {
      success
      customErrors {
        field
        code
        message
      }
    }
  }
`;

export const ChangeMarketingPipelineKindMutation = gql`
  mutation ChangeMarketingPipelineKind(
    $id: ID!
    $funnelKind: String!
    $locked: Boolean!
  ) {
    setMarketingPipelineKind(
      id: $id
      funnelKind: $funnelKind
      locked: $locked
    ) {
      success
      customErrors {
        field
        code
        message
      }
    }
  }
`;

/** Ручная привязка кампании / объявления к семейству и офферу. */
export const PinMarketingAdEntityMutation = gql`
  mutation PinMarketingAdEntity(
    $id: ID!
    $familySlug: String
    $offer: String
    $locked: Boolean!
  ) {
    overrideMarketingAdEntity(
      id: $id
      familySlug: $familySlug
      offer: $offer
      locked: $locked
    ) {
      success
      customErrors {
        field
        code
        message
      }
    }
  }
`;

/** Admin: ручная правка продажи (обязательная заметка — почему). */
export const CorrectMarketingSaleMutation = gql`
  mutation CorrectMarketingSale($input: MarketingSaleOverrideInput!) {
    overrideMarketingSale(input: $input) {
      success
      customErrors {
        field
        code
        message
      }
    }
  }
`;

/* ── Этап 2 ──────────────────────────────────────────────────────────── */

/**
 * Строка плана (`clear: true` — удалить). Сервер применяет её сразу
 * (поднимает `dataVersion` до ответа, пересборки нет) — план-факт виден на
 * первом же перечитывании.
 */
export const SetMarketingPlanTargetMutation = gql`
  mutation SetMarketingPlanTarget($input: MarketingTargetInput!) {
    setMarketingTarget(input: $input) {
      success
      customErrors {
        field
        code
        message
      }
    }
  }
`;

/** Решение по сигналу: `ack` — принято, `dismissed` — скрыть. */
export const DecideMarketingSignalMutation = gql`
  mutation DecideMarketingSignal($id: ID!, $status: String!) {
    ackMarketingSignal(id: $id, status: $status) {
      success
      customErrors {
        field
        code
        message
      }
    }
  }
`;

/**
 * Ручное сопоставление метки CRM (`utm_campaign` / `utm_id`) с кампанией
 * кабинета — точное, без нечёткого сравнения. Ставит пересборку.
 */
export const StoreMarketingCampaignAliasMutation = gql`
  mutation StoreMarketingCampaignAlias($input: MarketingCampaignAliasInput!) {
    saveMarketingCampaignAlias(input: $input) {
      success
      customErrors {
        field
        code
        message
      }
      alias {
        ...MarketingCampaignAliasFields
      }
    }
  }
  ${MARKETING_CAMPAIGN_ALIAS_FIELDS}
`;

export const RemoveMarketingCampaignAliasMutation = gql`
  mutation RemoveMarketingCampaignAlias($id: ID!) {
    deleteMarketingCampaignAlias(id: $id) {
      success
      customErrors {
        field
        code
        message
      }
    }
  }
`;

/**
 * Этап 3 — регулярка бренда поиска, ключевые события GA4 и хосты своего
 * сайта (Admin, Marketing). Не переданное поле не меняется. Смена бренда
 * или хостов переразмечает собранную историю поиска (пересчёт), список
 * событий действует только на новые сборы GA4.
 */
export const StoreMarketingSearchSettingsMutation = gql`
  mutation StoreMarketingSearchSettings(
    $brandRegex: String
    $ga4KeyEvents: [String!]
    $siteHosts: [String!]
  ) {
    saveMarketingSearchSettings(
      brandRegex: $brandRegex
      ga4KeyEvents: $ga4KeyEvents
      siteHosts: $siteHosts
    ) {
      success
      customErrors {
        field
        code
        message
      }
      brandRegex
      ga4KeyEvents
      siteHosts
    }
  }
`;

/* ── Этап 4 ──────────────────────────────────────────────────────────── */

/**
 * «Пересобрать» AI-сводку недели (Admin, Marketing): заводит строку `queued`
 * и ставит задачу — ответ сразу, текст появится, когда задача закончится.
 * Платный внешний вызов — экран спрашивает подтверждение. Отказы:
 * `digest_disabled` (флаг выключен), `digest_running` (эта неделя уже
 * готовится), `week_not_finished`, `bad_week`.
 */
export const QueueMarketingDigestRebuildMutation = gql`
  mutation QueueMarketingDigestRebuild($isoWeek: String) {
    generateMarketingDigest(isoWeek: $isoWeek) {
      success
      customErrors {
        field
        code
        message
      }
    }
  }
`;

/**
 * Правило «сигналы → задачи» (только Admin): виды, важность не ниже,
 * исполнители. Отказы: `bad_kind`, `bad_severity`, `bad_assignee` (нет
 * такого человека или он не видит задачи ERP).
 */
export const StoreMarketingSignalRoutingMutation = gql`
  mutation StoreMarketingSignalRouting($input: MarketingSignalRoutingInput!) {
    saveMarketingSignalRouting(input: $input) {
      success
      customErrors {
        field
        code
        message
      }
    }
  }
`;

/* ── ТЗ 0103, этап 3 ─────────────────────────────────────────────────── */

/**
 * «Типы страниц» (Admin, Marketing): правила `[{type, regex}]` целиком —
 * порядок задаёт приоритет, пустой список возвращает правила по умолчанию.
 * Сохранение переразмечает справочник страниц и ставит пересборку. Отказы —
 * по полю правила: `rules.<i>.type` (`bad_type`), `rules.<i>.regex`
 * (`required`, `too_long`, `bad_regex`, `backreference`,
 * `nested_quantifier`), `rules` (`too_many`).
 */
export const SaveMarketingPageTypesFormMutation = gql`
  mutation SaveMarketingPageTypesForm($rules: [MarketingPageTypeRuleInput!]!) {
    saveMarketingPageTypes(rules: $rules) {
      success
      customErrors {
        field
        code
        message
      }
      rules {
        type
        regex
      }
      changed
    }
  }
`;
